import { useState, useEffect, useCallback, forwardRef } from 'react';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';

import { Modal, ModalMethods, Text, Loader, toasts } from '../../../../../ui-component';
import { api } from '../../../../../utils/apiRequest';
import { getValueFromLocalStorage } from '../../../../../utils/common';

export type OutfitPieceType = {
  id: number;
  name: string;
  gender: string;
  outfit_type?: {
    name: string;
    ordinal: number;
    displayString: string;
    pieces: number;
    genderList: Array<{ string: string }>;
    subIndexString: string;
  };
  outfit_side?: {
    view: string;
    ordinal: number;
  };
  outfit_link?: string;
  icon_reference_id?: string;
};

type AddPieceModalProps = {
  onPieceSelect: (piece: OutfitPieceType) => void;
  onPieceCreate?: (pieceName: string) => void;
};

const AddPieceModal = forwardRef<ModalMethods, AddPieceModalProps>(
  ({ onPieceSelect, onPieceCreate }, ref) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [pieces, setPieces] = useState<OutfitPieceType[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isCreating, setIsCreating] = useState(false);

    const fetchPieces = useCallback(async () => {
      const boutique_id = getValueFromLocalStorage('boutique_id');

      if (!boutique_id) {
        toasts('error', 'Boutique ID not found', 'boutique-id-missing');

        return;
      }

      setIsLoading(true);

      try {
        const response = await api.getRequest(`outfit_piece/${boutique_id}`);

        if (response.status && !_isNil(response.data)) {
          const piecesData = Array.isArray(response.data) ? response.data : [];

          // Filter out pieces with empty names
          const validPieces = piecesData.filter(
            (piece: OutfitPieceType) => piece.name && !_isEmpty(piece.name.trim())
          );

          setPieces(validPieces);
        } else {
          toasts('error', response.message || 'Failed to fetch pieces', 'fetch-pieces-error');
          setPieces([]);
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'fetch-pieces-error');
        }
        setPieces([]);
      } finally {
        setIsLoading(false);
      }
    }, []);

    useEffect(() => {
      void fetchPieces();
    }, [fetchPieces]);

    const handlePieceClick = (piece: OutfitPieceType) => {
      onPieceSelect(piece);

      if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
        ref.current.hide();
      }
    };

    const handleCreatePiece = async () => {
      const trimmedQuery = searchQuery.trim();

      if (_isEmpty(trimmedQuery)) {
        toasts('error', 'Please enter a piece name', 'piece-name-required');

        return;
      }

      const boutique_id = getValueFromLocalStorage('boutique_id');

      if (!boutique_id) {
        toasts('error', 'Boutique ID not found', 'boutique-id-missing');

        return;
      }

      setIsCreating(true);

      try {
        // Generate a UUID-like icon_reference_id or use a default
        // For now, using the piece name as a reference
        const iconReferenceId = '';

        const response = await api.postRequest('outfit_piece/save', {
          name: trimmedQuery,
          boutique_id: Number(boutique_id),
          icon_reference_id: iconReferenceId,
        });

        if (response.status && !_isNil(response.data)) {
          const newPiece = response.data;

          if (onPieceCreate) {
            onPieceCreate(trimmedQuery);
          }

          onPieceSelect(newPiece);

          if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
            ref.current.hide();
          }

          toasts('success', 'Piece created successfully', 'piece-created-success');
        } else {
          toasts('error', response.message || 'Failed to create piece', 'create-piece-error');
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'create-piece-error');
        }
      } finally {
        setIsCreating(false);
      }
    };

    // Filter pieces based on search query
    const filteredPieces = pieces.filter((piece) => {
      const pieceName = piece.name.toLowerCase();
      const query = searchQuery.trim().toLowerCase();

      return _isEmpty(query) || pieceName.includes(query);
    });

    const isQueryInPieces = filteredPieces.some(
      (piece) => piece.name.toLowerCase() === searchQuery.trim().toLowerCase()
    );

    const showCreateButton = !_isEmpty(searchQuery.trim()) && !isQueryInPieces;

    return (
      <Modal
        ref={ref}
        size="xl"
        title="Select existing or add new piece"
        showFooter={false}
        showCloseIcon={true}
      >
        <div className="flex flex-col h-full min-h-[400px]">
          <div className="mb-1">
            <Text className="font-inter text-sm text-gray-600 mb-4 mt-1 block" color="black">
              Select any piece to import all its details. You can rename or edit it anytime.
            </Text>
            <div className="relative flex items-center w-full">
              <input
                type="text"
                placeholder="Search or type a new category"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-10 px-4 border border-gray-300 rounded-lg text-sm font-inter outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500"
                autoFocus
              />
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <Loader />
            </div>
          ) : (
            <>
              {filteredPieces.length > 0 ? (
                <div className="flex flex-col gap-2 py-2 max-h-[300px] overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-track]:rounded [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:hover:bg-gray-500">
                  {filteredPieces.map((piece) => {
                    return (
                      <div
                        key={piece.id}
                        onClick={() => handlePieceClick(piece)}
                        className="px-4 py-3 rounded-lg cursor-pointer transition-all hover:bg-gray-50 border border-transparent"
                      >
                        <Text size="medium" color="black" className="font-inter">
                          {piece.name}
                        </Text>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center justify-center h-[200px] p-5">
                  <Text color="gray" size="small" className="font-inter">
                    {searchQuery ? 'No pieces found matching your search' : 'No pieces available'}
                  </Text>
                </div>
              )}
            </>
          )}

          {showCreateButton && (
            <div className="mt-auto pb-5 border-t border-gray-300 pt-4">
              <button
                type="button"
                onClick={handleCreatePiece}
                disabled={isCreating}
                className="w-full px-4 py-4 bg-blue-600 text-white rounded-lg font-semibold transition-all hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isCreating ? (
                  <>
                    <Loader />
                    <Text size="small" color="white">
                      Creating...
                    </Text>
                  </>
                ) : (
                  <Text
                    color="white"
                    className="font-inter font-medium text-sm leading-none tracking-normal"
                  >
                    + Create &quot;{searchQuery.trim()}&quot; as new Piece
                  </Text>
                )}
              </button>
            </div>
          )}
        </div>
      </Modal>
    );
  }
);

AddPieceModal.displayName = 'AddPieceModal';

export default AddPieceModal;
